import {
	ApiResponse,
	UpdateVolunteersHighlightRequest,
	VolunteersHighlightFormData,
	VolunteersHighlightGetApiResponse,
	VolunteersHighlightListApiResponse,
} from "../context/VolunteersHighlightContext/constants";
import { axiosInstance } from "../utils/axiosInstance";

const ENDPOINTS = {
	VOLUNTEERS_HIGHLIGHT: "/cms/volunteers-highlight",
};

const LOCALIZABLE_PREFIX = "volunteersHighlightLocalizable";

interface LocalizableFormFields {
	highlightLocalizableId?: string;
	culture: string;
	title?: string;
	description?: string;
	image?: File | null;
}

// The backend names the stored image after the uploaded file, so two cultures (or a re-upload) with the same file
// name would overwrite each other on the CDN; prefixing the culture and upload time keeps every image at its own URL.
const toUploadFileName = (culture: string, file: File) => `${culture}-${Date.now()}-${file.name}`;

const appendText = (formData: FormData, key: string, value?: string) => {
	if (value !== undefined) {
		formData.append(key, value.trim());
	}
};

const appendLocalizable = (formData: FormData, index: number, item: LocalizableFormFields) => {
	const prefix = `${LOCALIZABLE_PREFIX}[${index}]`;
	if (item.highlightLocalizableId) {
		// The detail response calls this id highlightLocalizableId, the update request volunteersHighlightLocalizableId.
		formData.append(`${prefix}.volunteersHighlightLocalizableId`, item.highlightLocalizableId);
	}
	formData.append(`${prefix}.culture`, item.culture);
	appendText(formData, `${prefix}.title`, item.title);
	appendText(formData, `${prefix}.description`, item.description);
	if (item.image) {
		formData.append(`${prefix}.image`, item.image, toUploadFileName(item.culture, item.image));
	}
};

export const buildAddVolunteersHighlightFormData = (data: VolunteersHighlightFormData): FormData => {
	const formData = new FormData();

	formData.append("isActive", String(data.isActive));
	if (data.displayOrder !== undefined) {
		formData.append("displayOrder", String(data.displayOrder));
	}

	data.volunteersHighlightLocalizable.forEach((item, index) => appendLocalizable(formData, index, item));

	return formData;
};

export const buildUpdateVolunteersHighlightFormData = (request: UpdateVolunteersHighlightRequest): FormData => {
	const formData = new FormData();

	formData.append("highlightId", request.highlightId);
	if (request.isActive !== undefined) {
		formData.append("isActive", String(request.isActive));
	}
	if (typeof request.displayOrder === "number") {
		formData.append("displayOrder", String(request.displayOrder));
	}

	request.volunteersHighlightLocalizable?.forEach((item, index) => appendLocalizable(formData, index, item));

	return formData;
};

export class VolunteersHighlightService {
	static async getVolunteersHighlightList(): Promise<VolunteersHighlightListApiResponse> {
		const response = await axiosInstance.get(ENDPOINTS.VOLUNTEERS_HIGHLIGHT);
		return response.data;
	}

	static async getVolunteersHighlightDetails(highlightId: string): Promise<VolunteersHighlightGetApiResponse> {
		const response = await axiosInstance.get(`${ENDPOINTS.VOLUNTEERS_HIGHLIGHT}/${highlightId}`);
		return response.data;
	}

	static async addVolunteersHighlight(data: VolunteersHighlightFormData): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(ENDPOINTS.VOLUNTEERS_HIGHLIGHT, buildAddVolunteersHighlightFormData(data));
		return response.data;
	}

	static async updateVolunteersHighlight(request: UpdateVolunteersHighlightRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.patch(ENDPOINTS.VOLUNTEERS_HIGHLIGHT, buildUpdateVolunteersHighlightFormData(request));
		return response.data;
	}

	static async removeVolunteersHighlight(highlightId: string): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(`${ENDPOINTS.VOLUNTEERS_HIGHLIGHT}/${highlightId}`);

		if (response.status === 204) {
			return { result: null, error: null, isSuccessful: true };
		}

		return response.data;
	}
}
